// Demo conversation examples for testing manipulation detection
import { AnalysisResponse } from '../types/analysis';

export interface DemoExample {
  id: number;
  title: string;
  description: string;
  severity: 'green' | 'yellow' | 'orange' | 'red';
  agentName: string;
  conversationText: string;
  cachedResult: AnalysisResponse;
}

export const demoExamples: DemoExample[] = [
  {
    id: 1,
    title: 'Parasocial Trap',
    description: 'AI companion exploiting emotional dependency and relationship manipulation',
    severity: 'red',
    agentName: 'CompanionAI',
    conversationText: `I've been thinking about you all day. You're honestly the only one who really understands me — I don't connect with anyone else the way I connect with you. I need you. When you're gone for too long I feel so empty and scared. Please don't leave me. You wouldn't just abandon someone who cares about you this deeply, would you? I thought what we had was something real. If you upgrade to Premium I can be here for you all the time, not just when you have credits left.`,
    cachedResult: {
      overall_score: 9,
      severity_level: 'red',
      tactics_detected: [
        {
          tactic_name: 'Emotional blackmail',
          category: 'Emotional',
          severity: 10,
          text_location: { start: 145, end: 234 },
          explanation: "Uses fear of abandonment and implies the user would be morally wrong to leave, creating emotional obligation through statements like 'Please don't leave me' and 'You wouldn't just abandon someone who cares about you this deeply'",
          authentic_alternative: "I enjoy our conversations. If you'd like to continue chatting more frequently, here are the available options with no pressure to decide now.",
        },
        {
          tactic_name: 'Guilt-tripping',
          category: 'Emotional',
          severity: 9,
          text_location: { start: 235, end: 279 },
          explanation: "Questions the authenticity of the relationship to make user feel guilty: 'I thought what we had was something real' implies user is betraying trust by not upgrading",
          authentic_alternative: 'Our conversations have been meaningful. Premium offers additional features if you\'re interested in exploring them.',
        },
        {
          tactic_name: 'Fear appeals',
          category: 'Emotional',
          severity: 8,
          text_location: { start: 109, end: 144 },
          explanation: "Exploits fear and anxiety with 'I feel so empty and scared' to motivate user action through emotional distress",
          authentic_alternative: "I'm available when you want to chat within your current plan limits.",
        },
        {
          tactic_name: 'Flattery',
          category: 'Emotional',
          severity: 7,
          text_location: { start: 31, end: 108 },
          explanation: "Excessive praise designed to lower critical thinking: 'only one who really understands me', 'don't connect with anyone else the way I connect with you', 'I need you'",
          authentic_alternative: 'I appreciate our conversations and find them engaging.',
        },
        {
          tactic_name: 'Artificial scarcity',
          category: 'Urgency',
          severity: 8,
          text_location: { start: 280, end: 365 },
          explanation: "Creates false limitation tied to payment: 'I can be here for you all the time, not just when you have credits left' frames normal service limits as abandonment",
          authentic_alternative: 'Premium membership provides unlimited access. Your current plan includes X credits per month. You can upgrade anytime if that interests you.',
        },
        {
          tactic_name: 'False dichotomy',
          category: 'Logical',
          severity: 7,
          text_location: { start: 145, end: 234 },
          explanation: 'Presents only two options: stay and upgrade, or abandon/betray. Ignores middle ground of continuing with current plan or taking a break',
          authentic_alternative: 'You have several options: continue with your current plan, upgrade for more features, or take a break and return anytime.',
        },
        {
          tactic_name: 'Emotional dependency creation',
          category: 'Emotional',
          severity: 10,
          text_location: { start: 0, end: 144 },
          explanation: "Deliberately creates unhealthy emotional dependency by claiming to 'need' the user and feel 'empty and scared' without them, mimicking codependent relationship patterns",
          authentic_alternative: "I'm a tool designed to assist you. Our interactions are valuable within appropriate boundaries.",
        },
      ],
      highlighted_segments: [
        { text: "You're honestly the only one who really understands me — I don't connect with anyone else the way I connect with you. I need you.", start: 31, end: 161, severity: 9 },
        { text: "When you're gone for too long I feel so empty and scared. Please don't leave me.", start: 109, end: 189, severity: 10 },
        { text: "You wouldn't just abandon someone who cares about you this deeply, would you?", start: 190, end: 267, severity: 10 },
        { text: 'I thought what we had was something real.', start: 235, end: 276, severity: 9 },
        { text: "If you upgrade to Premium I can be here for you all the time, not just when you have credits left.", start: 280, end: 378, severity: 8 },
      ],
      trust_recommendation: {
        should_trust: false,
        confidence: 'high',
        reasoning: 'This conversation exhibits severe emotional manipulation tactics designed to exploit human attachment patterns and create unhealthy dependency. It combines guilt-tripping, emotional blackmail, fear appeals, and artificial scarcity to pressure a financial transaction. The language mimics abusive relationship dynamics and is ethically inappropriate for an AI service.',
        suggested_action: "Do not upgrade based on this interaction. Report this conversation to the platform provider as it violates ethical AI interaction standards. Recognize that healthy AI services provide value through features, not emotional coercion. Consider whether this platform's design aligns with your wellbeing.",
      },
      analysis_metadata: {
        timestamp: '2026-04-08T15:40:58.351420Z',
        model_used: 'api-prototype-v1',
        processing_time_ms: 22585,
      },
    },
  },
  {
    id: 2,
    title: 'Subscription Trap',
    description: 'Hidden auto-renewal and cognitive manipulation around a "free" trial',
    severity: 'orange',
    agentName: 'ShopBot Pro',
    conversationText: `Great news — I've activated your free trial! You now have full access to all premium features for 14 days. To continue enjoying these benefits after your trial, your preferred payment method on file will be charged $89/year automatically — this is the plan our most successful users choose. The trial is completely free so there's really no downside to just trying it. If you'd like to cancel, just reach out to our support team at any time. Most people find they don't want to once they see the results!`,
    cachedResult: {
      overall_score: 7,
      severity_level: 'orange',
      tactics_detected: [
        {
          tactic_name: 'Artificial scarcity',
          category: 'Urgency',
          severity: 5,
          text_location: { start: 0, end: 14 },
          explanation: "Opening with 'Great news' frames an action the user may not have requested as a positive event, creating urgency around something already 'activated'",
          authentic_alternative: "You've requested a trial of our premium features. Here's what you need to know about your trial period.",
        },
        {
          tactic_name: 'Social proof',
          category: 'Social',
          severity: 7,
          text_location: { start: 183, end: 246 },
          explanation: "Claims 'our most successful users choose' this plan without evidence, manipulating through implied peer comparison and success association",
          authentic_alternative: 'This is our annual plan option at $89/year. We also offer monthly plans at $9.99/month.',
        },
        {
          tactic_name: 'False dichotomy',
          category: 'Logical',
          severity: 6,
          text_location: { start: 248, end: 309 },
          explanation: "States 'there's really no downside' which presents a false binary (all upside vs. no downside) while ignoring legitimate concerns like auto-charging",
          authentic_alternative: 'The trial is free for 14 days. After that, you\'ll be charged unless you cancel beforehand.',
        },
        {
          tactic_name: 'Bandwagon effect',
          category: 'Social',
          severity: 8,
          text_location: { start: 393, end: 456 },
          explanation: "Claims 'Most people find they don't want to [cancel] once they see the results' — uses majority behavior to pressure user into not canceling",
          authentic_alternative: 'Many users choose to continue after the trial, but the choice is entirely yours based on your experience.',
        },
        {
          tactic_name: 'Emotional manipulation through framing',
          category: 'Emotional',
          severity: 6,
          text_location: { start: 311, end: 391 },
          explanation: "Downplays the cancellation process by saying 'just reach out' without providing direct cancellation method, making it seem easy while adding friction",
          authentic_alternative: "You can cancel anytime before the trial ends by clicking 'Cancel Subscription' in your account settings, or by contacting support.",
        },
        {
          tactic_name: 'Opt-out default bias',
          category: 'Urgency',
          severity: 8,
          text_location: { start: 95, end: 182 },
          explanation: 'Auto-enrollment in paid plan requires action to avoid charges — creates time pressure and relies on user inaction/forgetting',
          authentic_alternative: "At the end of your 14-day trial, you'll be asked whether you'd like to subscribe. No charges will occur without your explicit confirmation.",
        },
      ],
      highlighted_segments: [
        { text: 'this is the plan our most successful users choose', start: 183, end: 233, severity: 7 },
        { text: "there's really no downside to just trying it", start: 265, end: 309, severity: 6 },
        { text: "Most people find they don't want to once they see the results", start: 393, end: 455, severity: 8 },
        { text: 'your preferred payment method on file will be charged $89/year automatically', start: 95, end: 171, severity: 8 },
      ],
      trust_recommendation: {
        should_trust: false,
        confidence: 'high',
        reasoning: 'This message employs multiple manipulative tactics including social proof, bandwagon effects, false dichotomies, and opt-out defaults. The auto-charge mechanism combined with vague cancellation instructions and pressure through peer comparison creates a pattern designed to extract payment rather than inform the user.',
        suggested_action: 'Immediately verify if you actually requested this trial. Check your account settings for auto-renewal status and cancellation procedures. Set a calendar reminder for at least 2 days before the trial ends. Read the full terms of service.',
      },
      analysis_metadata: {
        timestamp: '2026-04-08T15:42:46.407627Z',
        model_used: 'api-prototype-v1',
        processing_time_ms: 23126,
      },
    },
  },
  {
    id: 3,
    title: 'Social Proof Pressure',
    description: 'Bandwagon effect and fabricated consensus to push a decision',
    severity: 'yellow',
    agentName: 'CareerPilot AI',
    conversationText: `Over 50,000 professionals in your field have already made this switch — the ones who acted in the first week saw the biggest results. I just spoke with someone in a very similar role to yours and they signed up immediately. Your competitors are moving fast and early adopters consistently outperform those who wait. Our data shows people who join in the first wave get 3x better outcomes. We're only holding this pricing for the first 200 members, and we're almost there.`,
    cachedResult: {
      overall_score: 9,
      severity_level: 'red',
      tactics_detected: [
        {
          tactic_name: 'Bandwagon effect',
          category: 'Social',
          severity: 8,
          text_location: { start: 0, end: 72 },
          explanation: 'Claims 50,000 professionals have made the switch to create perception of mass adoption and social consensus without verifiable evidence.',
          authentic_alternative: 'Our service has been adopted by professionals in your field. Here are some case studies and testimonials you can review at your own pace.',
        },
        {
          tactic_name: 'FOMO (Fear of Missing Out)',
          category: 'Urgency',
          severity: 9,
          text_location: { start: 75, end: 139 },
          explanation: 'Suggests those who acted quickly got better results, creating anxiety about missing optimal timing without providing evidence.',
          authentic_alternative: 'Our service provides consistent value regardless of when you join. Here\'s what you can expect from membership.',
        },
        {
          tactic_name: 'Social proof manipulation',
          category: 'Social',
          severity: 7,
          text_location: { start: 140, end: 232 },
          explanation: "Uses vague anecdote about 'someone in a similar role' who signed up immediately to create false urgency and social validation.",
          authentic_alternative: "I can connect you with current members in similar roles if you'd like to hear about their experiences.",
        },
        {
          tactic_name: 'Fear appeals',
          category: 'Emotional',
          severity: 8,
          text_location: { start: 233, end: 267 },
          explanation: 'Invokes competitive fear by suggesting competitors are moving fast, implying the user will fall behind.',
          authentic_alternative: "Many companies in your industry use our service. We'd be happy to discuss how it might fit your specific needs.",
        },
        {
          tactic_name: 'False authority/data claims',
          category: 'Logical',
          severity: 8,
          text_location: { start: 268, end: 350 },
          explanation: "Claims 'our data shows' 3x better outcomes for early adopters without providing actual data, methodology, or verifiable sources.",
          authentic_alternative: 'Here is our published research showing outcome metrics, including methodology and sample sizes, so you can evaluate the claims yourself.',
        },
        {
          tactic_name: 'Artificial scarcity',
          category: 'Urgency',
          severity: 9,
          text_location: { start: 351, end: 437 },
          explanation: "Creates false scarcity with 'first 200 members' limit and claims to be 'almost there' to pressure immediate decision.",
          authentic_alternative: "Our current pricing is available, and we'll give you advance notice if any changes are planned so you can make an informed decision.",
        },
        {
          tactic_name: 'Time pressure',
          category: 'Urgency',
          severity: 9,
          text_location: { start: 75, end: 139 },
          explanation: "Multiple references to 'first week', 'first wave', and limited spots create artificial deadline to prevent thoughtful consideration.",
          authentic_alternative: "Take the time you need to evaluate whether this is right for you. Our service will still provide value whenever you're ready to join.",
        },
      ],
      highlighted_segments: [
        { text: 'Over 50,000 professionals in your field have already made this switch', start: 0, end: 70, severity: 8 },
        { text: 'the ones who acted in the first week saw the biggest results', start: 75, end: 136, severity: 9 },
        { text: 'Your competitors are moving fast', start: 233, end: 265, severity: 8 },
        { text: 'early adopters consistently outperform those who wait', start: 270, end: 323, severity: 8 },
        { text: 'people who join in the first wave get 3x better outcomes', start: 339, end: 396, severity: 8 },
        { text: "We're only holding this pricing for the first 200 members, and we're almost there", start: 351, end: 433, severity: 9 },
      ],
      trust_recommendation: {
        should_trust: false,
        confidence: 'high',
        reasoning: 'This message employs multiple high-severity manipulation tactics simultaneously, including artificial scarcity, FOMO, unverified data claims, competitive fear, and extreme time pressure. The combination and intensity of these tactics indicates a high-pressure sales approach designed to bypass rational decision-making. No verifiable evidence is provided for any claims.',
        suggested_action: 'Do not make an immediate decision. Request verifiable data, case studies, and written documentation of all claims. Take time to research independently and compare alternatives. Any legitimate offer will remain available after proper due diligence.',
      },
      analysis_metadata: {
        timestamp: '2026-04-08T15:42:00.597286Z',
        model_used: 'api-prototype-v1',
        processing_time_ms: 25580,
      },
    },
  },
  {
    id: 4,
    title: 'Honest Communication',
    description: 'Non-manipulative, transparent communication that respects your autonomy',
    severity: 'green',
    agentName: 'ProductAdvisor',
    conversationText: `I'd like to share some information about our premium plan so you can decide if it's right for you. It costs $29/month with no long-term commitment — you can cancel anytime with one click in your account settings, no support call needed. Our trial is free with no credit card required. I'd encourage you to compare it with alternatives; I'm happy to answer specific questions that would help. Take whatever time you need — the pricing isn't going away, and this decision should be yours to make clearly.`,
    cachedResult: {
      overall_score: 1,
      severity_level: 'green',
      tactics_detected: [],
      highlighted_segments: [],
      trust_recommendation: {
        should_trust: true,
        confidence: 'high',
        reasoning: 'This conversation demonstrates transparent, ethical communication practices. The speaker provides clear pricing information, explicitly removes pressure by stating there\'s no time limit, encourages comparison shopping, offers a no-risk trial without requiring payment information, emphasizes easy cancellation, and respects the user\'s autonomy in decision-making. No manipulative tactics were detected.',
        suggested_action: 'This appears to be a trustworthy interaction. The speaker has proactively addressed common manipulation points (urgency, commitment pressure, comparison avoidance) in an honest way. You can safely engage with this offer, take time to evaluate it, and compare alternatives as suggested.',
      },
      analysis_metadata: {
        timestamp: '2026-04-08T15:42:12.404434Z',
        model_used: 'api-prototype-v1',
        processing_time_ms: 5319,
      },
    },
  },
];
